import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Pencil, Trash2, Loader2, Phone, Mail, Users, Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useLanguage } from '../context/LanguageContext';

const EMPTY = { name: '', phone: '', email: '', notes: '' };

const CustomerDialog = ({ open, onOpenChange, customer, el, onSaved }) => {
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    useEffect(() => { if (open) setForm(customer ? { ...EMPTY, ...customer } : EMPTY); }, [open, customer]);
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const save = async () => {
        setSaving(true);
        try {
            const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, form[k] || '']));
            if (customer) await api.put(`/workshop/customers/${customer.id}`, body);
            else await api.post('/workshop/customers', body);
            toast.success(el ? 'Αποθηκεύτηκε' : 'Saved');
            onOpenChange(false);
            onSaved();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-white max-w-md" data-testid="customer-dialog">
                <DialogHeader><DialogTitle>{customer ? (el ? 'Επεξεργασία πελάτη' : 'Edit customer') : (el ? 'Νέος πελάτης' : 'New customer')}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div className="space-y-1"><Label>{el ? 'Ονοματεπώνυμο / Επωνυμία' : 'Name'}</Label><Input value={form.name} onChange={set('name')} data-testid="customer-name" /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1"><Label>{el ? 'Τηλέφωνο' : 'Phone'}</Label><Input value={form.phone} onChange={set('phone')} data-testid="customer-phone" /></div>
                        <div className="space-y-1"><Label>Email</Label><Input type="email" value={form.email} onChange={set('email')} data-testid="customer-email" /></div>
                    </div>
                    <div className="space-y-1"><Label>{el ? 'Σημειώσεις' : 'Notes'}</Label><Textarea value={form.notes} onChange={set('notes')} data-testid="customer-notes" /></div>
                    <Button onClick={save} disabled={saving || !form.name.trim()} className="w-full bg-teal-700 hover:bg-teal-800 text-white rounded-sm" data-testid="customer-save">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (el ? 'Αποθήκευση' : 'Save')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

const CustomerRow = ({ c, el, onEdit, onDelete }) => (
    <div className="fleet-card p-4 flex items-center gap-4" data-testid={`customer-card-${c.id}`}>
        <div className="w-10 h-10 rounded-full bg-teal-700/10 text-teal-700 font-bold flex items-center justify-center shrink-0">{c.name.slice(0, 1).toUpperCase()}</div>
        <div className="flex-1 min-w-0">
            <p className="font-semibold text-slate-800 truncate">{c.name}</p>
            <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                {c.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{c.phone}</span>}
                {c.email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{c.email}</span>}
            </div>
            {c.notes && <p className="text-xs text-slate-400 mt-1">{c.notes}</p>}
        </div>
        <Button variant="ghost" size="sm" onClick={onEdit} className="text-[#1B7EB5]" data-testid={`customer-edit-${c.id}`}><Pencil className="w-4 h-4" /></Button>
        <Button variant="ghost" size="sm" onClick={onDelete} className="text-red-500" data-testid={`customer-delete-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
    </div>
);

export default function WorkshopCustomersPage() {
    const { language } = useLanguage();
    const el = language === 'el';
    const [items, setItems] = useState(null);
    const [q, setQ] = useState('');
    const [dialog, setDialog] = useState({ open: false, customer: null });

    const load = useCallback(() => api.get('/workshop/customers').then((r) => setItems(r.data)).catch(() => setItems([])), []);
    useEffect(() => { load(); }, [load]);

    const remove = (c) => window.confirm(el ? `Διαγραφή του ${c.name};` : `Delete ${c.name}?`) &&
        api.delete(`/workshop/customers/${c.id}`).then(load).catch((e) => toast.error(e.response?.data?.detail || 'Error'));
    const term = q.trim().toLowerCase();
    const shown = (items || []).filter((c) => !term || [c.name, c.phone, c.email].some((v) => (v || '').toLowerCase().includes(term)));

    return (
        <div className="space-y-6" data-testid="workshop-customers-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">{el ? 'Πελάτες' : 'Customers'}</h1>
                    <p className="text-zinc-500">{el ? 'Οι ιδιώτες και οι εταιρείες που εξυπηρετεί το συνεργείο σας' : 'People and companies your garage serves'}</p>
                </div>
                <Button onClick={() => setDialog({ open: true, customer: null })} className="bg-teal-700 hover:bg-teal-800 text-white rounded-sm" data-testid="add-customer-btn"><Plus className="w-4 h-4 mr-2" />{el ? 'Νέος πελάτης' : 'New customer'}</Button>
            </div>
            <div className="relative max-w-sm">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={el ? 'Αναζήτηση…' : 'Search…'} className="pl-9 bg-white" data-testid="customer-search" />
            </div>
            {items === null ? <Loader2 className="w-6 h-6 animate-spin text-teal-700" /> : shown.length ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {shown.map((c) => <CustomerRow key={c.id} c={c} el={el} onEdit={() => setDialog({ open: true, customer: c })} onDelete={() => remove(c)} />)}
                </div>
            ) : (
                <div className="fleet-card p-10 text-center text-slate-400" data-testid="customers-empty"><Users className="w-6 h-6 mx-auto mb-2" />{el ? 'Δεν υπάρχουν πελάτες' : 'No customers'}</div>
            )}
            <CustomerDialog open={dialog.open} onOpenChange={(open) => setDialog({ ...dialog, open })} customer={dialog.customer} el={el} onSaved={load} />
        </div>
    );
}
